// /Users/anandarajl/Desktop/my-app/src/api/admin/userAccounts.api.js

/**
 * =============================================================
 * AADHAV ACADEMY – ADMIN USER ACCOUNTS API
 * Role: Admin
 * Scope: Tenant-based user management (Hierarchy + Roles)
 * Standard: Enterprise / MNC Level
 * =============================================================
 */

import { apiCall } from "../core/apiCall";

/* =============================================================
   BASE ENDPOINT
============================================================= */
const ADMIN_USER_ENDPOINT = "/admin/user-accounts/index.php";

/* =============================================================
   GET – Fetch User Accounts
   Supports: pagination, search
============================================================= */
export const getAdminUserAccounts = async (
  { page = 1, limit = 10, search = "" } = {},
  options = {}
) => {
  try {
    const res = await apiCall(
      "GET",
      ADMIN_USER_ENDPOINT,
      null,
      "Admin – Fetch User Accounts",
      {
        params: { page, limit, search },
        ...options,
      }
    );

    // Validate string status "success" or boolean true fallback
    if (!res || (res.status !== "success" && res.status !== true)) {
      return {
        success: false,
        message: res?.message || "Failed to fetch users",
        data: [],
        meta: {},
      };
    }

    return {
      success: true,
      data: res.data || [],
      meta: res.meta || {
        total: 0,
        page: 1,
        limit,
        total_pages: 0,
      },
    };

  } catch (error) {
    console.error("Admin Fetch User Accounts Error:", error);
    return {
      success: false,
      message: error.message || "Unexpected error occurred",
      data: [],
      meta: {},
    };
  }
};


/* =============================================================
   POST – Provision/Create New Enterprise User Account
   Payload: FormData containing full account profiles + image asset binary
============================================================= */
export const createAdminUserAccount = async (formDataPayload, options = {}) => {
  try {
    const res = await apiCall(
      "POST",
      ADMIN_USER_ENDPOINT,
      formDataPayload,
      "Admin – Create User Account",
      options
    );

    if (!res || (res.status !== "success" && res.status !== true)) {
      return {
        success: false,
        message: res?.message || "Failed to create user account",
      };
    }

    return {
      success: true,
      message: res.message || "User account provisioned successfully",
      data: res.data,
    };

  } catch (error) {
    console.error("Admin Create User Account Error:", error);
    return {
      success: false,
      message: error?.message || "Unexpected error occurred during provisioning",
    };
  }
};


/* =============================================================
   PUT / POST – Update Credentials / Force Reset Password / Modify Profile
   URL: /admin/user-accounts/index.php?user_id=USER_ID
   Payload: FormData (Since payload contains dynamic binary image files, 
             the execution route paths are safely directed via POST)
============================================================= */
export const updateAdminUserCredentials = async (userId, formDataPayload, options = {}) => {
  try {
    if (!userId) {
      return {
        success: false,
        message: "Invalid or missing user ID parameter",
      };
    }

    const res = await apiCall(
      "POST", 
      `${ADMIN_USER_ENDPOINT}?user_id=${userId}`, // Route targeting global users table primary key
      formDataPayload,
      "Admin – Update User Credentials",
      options
    );

    if (!res || (res.status !== "success" && res.status !== true)) {
      return {
        success: false,
        message: res?.message || "Failed to update user credentials",
      };
    }

    return {
      success: true,
      message: res.message || "User credentials synchronized successfully",
    };

  } catch (error) {
    console.error("Admin Update User Credentials Error:", error);
    return {
      success: false,
      message: error?.message || "Unexpected error occurred updating credentials",
    };
  }
};


/* =============================================================
   PUT – Enable / Disable User Status Flag
   URL: /admin/user-accounts/index.php?id=ID
   Payload: { is_active: 1/0 }
============================================================= */
export const updateAdminUserStatus = async (
  id,
  isActive,
  options = {}
) => {
  try {
    if (!id) {
      return {
        success: false,
        message: "Invalid user role mapping ID",
      };
    }

    const res = await apiCall(
      "PUT",
      `${ADMIN_USER_ENDPOINT}?id=${id}`,  // Route targeting user_org_roles mapping table ID
      { is_active: isActive ? 1 : 0 },
      "Admin – Update User Status",
      options
    );

    if (!res || (res.status !== "success" && res.status !== true)) {
      return {
        success: false,
        message: res?.message || "Failed to update user status",
      };
    }

    return {
      success: true,
      message: res.message || "User status updated successfully",
    };

  } catch (error) {
    console.error("Admin Update User Status Error:", error);
    return {
      success: false,
      message: error?.message || "Unexpected error occurred",
    };
  }
};


/* =============================================================
   GET – Fetch Single User Account (Optional Extension)
   URL: /admin/user-accounts/index.php?id=ID
============================================================= */
export const getAdminUserById = async (id, options = {}) => {
  try {
    if (!id) {
      return {
        success: false,
        message: "Invalid user ID",
      };
    }

    const res = await apiCall(
      "GET",
      `${ADMIN_USER_ENDPOINT}?id=${id}`,
      null,
      "Admin – Fetch Single User",
      options
    );

    if (!res || (res.status !== "success" && res.status !== true)) {
      return {
        success: false,
        message: res?.message || "Failed to fetch user",
      };
    }

    return {
      success: true,
      data: res.data,
    };

  } catch (error) {
    console.error("Admin Fetch User By ID Error:", error);
    return {
      success: false,
      message: error.message || "Unexpected error occurred",
    };
  }
};